import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import Button from "components/Button";
import { useNavigate } from "react-router-dom";
import Menu, { type MenuProps } from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import useAnchorElement from "hooks/useAnchorElement";
import PlusIcon from "components/Icons/PlusIcon";
import { styled } from "@mui/material";
import ListItemIcon from "@mui/material/ListItemIcon";
import Typography from "components/Typography";
import useCanDo from "hooks/useCanDo";
import QrCodeIcon from "components/Icons/QrCodeIcon";

const NavigationMenu = styled((props: MenuProps) => (
  <Menu
    elevation={0}
    anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
    transformOrigin={{ vertical: -2, horizontal: "right" }}
    {...props}
  />
))(({ theme }) => ({
  "& .MuiPaper-root": {
    backgroundColor: theme.palette.primary.main,
    boxShadow: theme.shadows[4],

    "& .MuiMenu-list": {
      padding: "4px 0",
    },

    "& .MuiMenuItem-root": {
      padding: "8px 12px",

      "& .MuiListItemIcon-root": {
        minWidth: "28px",
      },
      "& > .MuiTypography-root, .MuiSvgIcon-root": {
        color: theme.palette.common.white,
      },

      ":hover": {
        backgroundColor: "transparent",
        "& > .MuiTypography-root, .MuiSvgIcon-root": {
          color: theme.palette.secondary1[60],
        },
      },
    },
  },
}));

const ENABLE_ATHLETE_INVITE = JSON.parse(process.env.REACT_APP_ENABLE_ATHLETE_INVITE ?? "false");

function HeaderActions() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const bulkUploadEnabled = useCanDo("athlete.bulkUpload");

  const { onSetAnchorElement, anchorElement, onRemoveAnchorElement } = useAnchorElement<HTMLButtonElement>();

  const handleAddIndividualAthlete = useCallback(() => {
    navigate("/athlete-hub/add");
  }, [navigate]);

  const handleAddMultipleAthletes = useCallback(() => {
    navigate("/bulk-upload");
  }, [navigate]);

  const handleCreateInvite = useCallback(() => {
    navigate("/settings/athlete-invite");
  }, [navigate]);

  const open = Boolean(anchorElement);
  const id = open ? "add-athletes-menu" : undefined;

  const navigationMenuSlotProps = useMemo(() => {
    return {
      list: { "aria-labelledby": id },
    };
  }, [id]);

  if (!bulkUploadEnabled) {
    return (
      <Button size="small" startIcon={<PlusIcon fontSize="small" />} onClick={handleAddIndividualAthlete}>
        {t("athlete.add_athlete")}
      </Button>
    );
  }

  return (
    <>
      <Button
        id={id}
        size="small"
        startIcon={<PlusIcon fontSize="small" />}
        onClick={onSetAnchorElement}
        aria-controls={id}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
      >
        {t("global.add_athletes_label")}
      </Button>
      <NavigationMenu
        open={open}
        onClose={onRemoveAnchorElement}
        anchorEl={anchorElement}
        slotProps={navigationMenuSlotProps}
      >
        <MenuItem onClick={handleAddIndividualAthlete}>
          <ListItemIcon>
            <PlusIcon fontSize="small" />
          </ListItemIcon>
          <Typography variant="TextS" sx={{ fontWeight: "fontWeightMedium" }}>
            {t("global.individual_athlete_label")}
          </Typography>
        </MenuItem>
        <MenuItem onClick={handleAddMultipleAthletes}>
          <ListItemIcon>
            <PlusIcon fontSize="small" />
          </ListItemIcon>
          <Typography variant="TextS" sx={{ fontWeight: "fontWeightMedium" }}>
            {t("global.multiple_athletes_label")}
          </Typography>
        </MenuItem>
        {ENABLE_ATHLETE_INVITE && (
          <MenuItem onClick={handleCreateInvite}>
            <ListItemIcon>
              <QrCodeIcon fontSize="small" />
            </ListItemIcon>
            <Typography variant="TextS" sx={{ fontWeight: "fontWeightMedium" }}>
              {t("global.create_invite_label")}
            </Typography>
          </MenuItem>
        )}
      </NavigationMenu>
    </>
  );
}

export default HeaderActions;
